<template>
  <section
    class="fragments-page"
    :class="{ 'is-category-transitioning': categoryTransitionActive, 'is-note-preview': noteHoverPreview }"
  >
    <header class="fragments-hero" data-scroll-reveal>
      <div class="fragments-hero-copy">
        <h1>{{ copy.heading }}</h1>
        <p class="fragments-intro">{{ copy.intro }}</p>
      </div>
      <div class="fragments-hero-image" data-scroll-reveal>
        <img src="/assets/img/fragments-textured.png" alt="Harbour, ferry, rain and evening light layered with textured paper and handwritten fragments" width="2172" height="724" loading="eager" decoding="async">
      </div>
      <div class="fragments-controls" aria-label="Fragment tools">
        <div
          class="category-segmented"
          ref="categoryControl"
          :style="categoryMarkerStyle"
          :class="{ life: activeCategory === 'life', 'is-instant': categoryKeyboard }"
          role="group"
          aria-label="Fragment category"
        >
          <span class="craft-category-marker" :style="{ viewTransitionName: categoryTransitionActive ? 'fragment-category-marker' : 'none' }" aria-hidden="true"></span>
          <button
            type="button"
            class="category-option category-ideas"
            :aria-pressed="(activeCategory === 'ideas').toString()"
            @click="categoryKeyboard=$event.detail===0;setCategory('ideas')"
          >
            <span>Ideas</span>
            <span class="category-count">{{ ideaCount }}</span>
          </button>
          <button
            type="button"
            class="category-option category-life"
            :aria-pressed="(activeCategory === 'life').toString()"
            @click="categoryKeyboard=$event.detail===0;setCategory('life')"
          >
            <span>Life</span>
            <span class="category-count">{{ lifeCount }}</span>
          </button>
        </div>
        <label class="fragment-search" aria-label="Search fragments">
          <input
            v-model="searchQuery"
            class="fragment-search-input"
            type="search"
            autocomplete="off"
            spellcheck="false"
            placeholder="Search fragments"
            @keydown.esc.prevent="clearSearch"
          >
          <i class="fa fa-search fragment-search-icon" aria-hidden="true"></i>
        </label>
      </div>
    </header>

    <div class="fragment-list" data-scroll-group data-scroll-step="120">
      <article
        v-for="(fragment, fragmentIndex) in filteredFragments"
        :id="fragment.id"
        :key="fragment.id"
        class="fragment-card"
        :class="{ 'is-lifted': isOpen(fragment.id), 'is-restoring': restoringFragmentId === fragment.id }"
        :style="{
          viewTransitionName: categoryTransitionActive
            ? `fragment-category-card-${fragmentIndex}`
            : 'none'
        }"
        data-scroll-reveal
      >
        <div
          class="fragment-summary"
          data-press-intent="custom"
          :class="{ 'is-pressed': pressedFragmentId === fragment.id }"
          role="button"
          tabindex="0"
          :aria-expanded="isOpen(fragment.id).toString()"
          @pointerdown="startFragmentPress(fragment.id, $event)"
          @pointerup="finishFragmentPress(fragment.id)"
          @pointercancel="cancelFragmentPress(fragment.id)"
          @click="toggleFragment(fragment.id)"
          @keydown.enter.prevent="toggleFragment(fragment.id)"
          @keydown.space.prevent="toggleFragment(fragment.id)"
        >
          <div class="fragment-entry-surface">
            <h2 v-html="highlightText(fragment.title)"></h2>
            <p class="fragment-teaser" v-html="highlightText(fragment.teaser)"></p>
            <span class="read-cue">
              {{ copy.read }}
              <span v-if="noteHoverPreview" class="fragment-preview-mark" aria-hidden="true">
                <span class="fragment-read-arrow"><ArrowUpRightIcon /></span>
                <svg class="fragment-preview-note" viewBox="0 0 24 28" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 3h11l5 5v17H4Z" /><path d="M15 3v5h5M8 12h8M8 16h8M8 20h5" />
                </svg>
              </span>
              <template v-else>
                <span class="fragment-read-arrow" aria-hidden="true"><ArrowUpRightIcon /></span>
              </template>
            </span>
          </div>
        </div>

      </article>
    </div>
    <p v-if="searchActive && !filteredFragments.length" class="fragments-empty">
      No matching fragments.
    </p>

    <Teleport to="body">
      <div
        v-if="fragmentSurfaceActive && activeFragment"
        ref="fragmentOverlay"
        class="fragment-overlay"
        role="presentation"
        @click.self="closeFragment"
      >
        <article
          ref="floatingPaper"
          class="fragment-floating-paper"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="`floating-${activeFragment.id}`"
        >
          <button type="button" class="fragment-floating-close" aria-label="Close fragment" @click="closeFragment">×</button>
          <p class="fragment-floating-kind">{{ activeFragment.category === 'life' ? 'Life fragment' : 'Idea fragment' }}</p>
          <h2 :id="`floating-${activeFragment.id}`" v-html="highlightText(activeFragment.title)"></h2>
          <p class="fragment-floating-teaser" v-html="highlightText(activeFragment.teaser)"></p>
          <div class="fragment-floating-body">
            <p
              v-for="(paragraph, index) in activeFragment.body"
              :key="`${activeFragment.id}-floating-${index}`"
              v-html="highlightText(paragraph)"
            ></p>
            <SourceViewLinks :source-id="`fragments:${activeFragment.id}`" />
          </div>
        </article>
      </div>
    </Teleport>
  </section>
</template>

<script>
import { acquireInteractiveCursorLock } from '../utils/interactiveCursorLock';
import { fragments } from '../data/fragments';
import { settleScrollToId } from '../utils/anchorLanding';
import scrollRevealMixin from '../mixins/scrollRevealMixin';
import SourceViewLinks from './ui/SourceViewLinks.vue';

function shuffleFragments(items) {
  const shuffled = [...items];

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

export default {
  name: 'FragmentsTab',
  components: { SourceViewLinks },
  mixins: [scrollRevealMixin],
  data() {
    return {
      activeCategory: 'ideas',
      categoryKeyboard: false,
      categoryMarkerStyle: {},
      fragmentSurfaceActive: true,
      openIds: [],
      pressedFragmentId: '',
      restoringFragmentId: '',
      categoryTransitionActive: false,
      searchQuery: '',
      shuffledFragments: shuffleFragments(fragments)
    };
  },
  computed: {
    noteHoverPreview() {
      return process.env.NODE_ENV === 'development' && this.$route.query.hoverPreview === 'note';
    },
    ideaFragments() {
      return this.shuffledFragments.filter((fragment) => fragment.category === 'ideas');
    },
    lifeFragments() {
      return this.shuffledFragments.filter((fragment) => fragment.category === 'life');
    },
    ideaCount() {
      return this.searchActive
        ? this.ideaFragments.filter((fragment) => this.fragmentMatches(fragment)).length
        : this.ideaFragments.length;
    },
    lifeCount() {
      return this.searchActive
        ? this.lifeFragments.filter((fragment) => this.fragmentMatches(fragment)).length
        : this.lifeFragments.length;
    },
    copy() {
      return {
        heading: 'Fragments',
        intro: 'I write down thoughts and memories before they fade, so I can return to what I was thinking and how life felt at the time. You’re welcome to look through them and get to know me a little beyond my work.',
        read: 'Read',
        close: 'Close'
      };
    },
    filteredFragments() {
      const categoryFragments = this.activeCategory === 'life' ? this.lifeFragments : this.ideaFragments;
      if (this.searchActive) {
        return categoryFragments.filter((fragment) => this.fragmentMatches(fragment));
      }
      return categoryFragments;
    },
    activeFragment() {
      return this.openIds.length ? this.fragmentById(this.openIds[0]) : null;
    },
    searchActive() {
      return this.searchTerms.length > 0;
    },
    searchTerms() {
      return this.searchQuery
        .toLowerCase()
        .split(/[\s,.;:!?()[\]{}"']+/)
        .map((term) => term.trim())
        .filter((term) => term.length >= 2);
    }
  },
  watch: {
    '$route.hash'() {
      this.activateHashFragment();
    },
    searchQuery() {
      if (this.searchActive) {
        this.openIds = [];
      }
      this.refreshScrollReveal();
    },
    activeCategory() {
      this.refreshScrollReveal();
      this.$nextTick(this.updateCategoryMarker);
    }
  },
  created() {
    // The category must be switched and the card opened before it can be
    // scrolled into view; mounted() then owns the landing for this tab.
    this.activateHashFragment(false);
  },
  mounted() {
    this._fragmentAnims = new Set();
    this._categoryObserver = new ResizeObserver(this.updateCategoryMarker);
    this._categoryObserver.observe(this.$refs.categoryControl);
    this.$nextTick(this.updateCategoryMarker);
    this.activateHashFragment();
    this.attachFragmentEvents();
  },
  activated() {
    this.$nextTick(this.updateCategoryMarker);
    this.fragmentSurfaceActive = true;
    this.attachFragmentEvents();
    this.activateHashFragment();
  },
  deactivated() {
    this.releaseFragmentPresentation();
  },
  beforeUnmount() {
    this._categoryObserver?.disconnect();
    this.releaseFragmentPresentation();
  },
  methods: {
    updateCategoryMarker() {
      const control = this.$refs.categoryControl;
      const active = control?.querySelector('[aria-pressed="true"]');
      if (!active?.offsetWidth) return;
      this.categoryMarkerStyle = {
        '--category-marker-width': `${active.offsetWidth}px`,
        '--category-marker-left': `${active.offsetLeft}px`,
      };
    },
    attachFragmentEvents() {
      window.addEventListener('hashchange', this.activateHashFragment);
      window.addEventListener('keydown', this.handleFragmentKeydown);
    },
    releaseFragmentPresentation() {
      // A cached tab remains mounted. Its teleported paper and pending work
      // must leave the page as soon as the tab loses ownership of the route.
      this.fragmentSurfaceActive = false;
      this.openIds = [];
      this.pressedFragmentId = '';
      this.restoringFragmentId = '';
      this._fragmentPresentationVersion = (this._fragmentPresentationVersion || 0) + 1;
      this._fragmentReturnTarget = null;
      this._fragmentSourceRect = null;
      this._fragmentPressCandidateId = '';
      this._fragmentReleasedPressId = '';
      this._fragmentActivationDelay = 0;
      window.removeEventListener('hashchange', this.activateHashFragment);
      window.removeEventListener('keydown', this.handleFragmentKeydown);
      for (const timer of ['_fragmentPressTimer', '_fragmentPressIntentTimer', '_fragmentOpenTimer', '_fragmentLandingTimer']) {
        window.clearTimeout(this[timer]);
      }
      window.cancelAnimationFrame(this._fragmentOpenFrame);
      this._fragmentAnims?.forEach(animation => animation.cancel());
      this._fragmentAnims?.clear();
      this._categoryTransitionToken = (this._categoryTransitionToken || 0) + 1;
      this._categoryTransition?.skipTransition?.();
      this._categoryTransition = null;
      this._releaseCategoryCursorLock?.();
      this._releaseCategoryCursorLock = null;
      this.categoryTransitionActive = false;
      document.documentElement.style.removeProperty('--fragment-category-direction');
    },
    startFragmentPress(id, event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      window.clearTimeout(this._fragmentPressTimer);
      window.clearTimeout(this._fragmentPressIntentTimer);
      if (event.currentTarget && event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      this._fragmentPressStartedAt = window.performance ? window.performance.now() : Date.now();
      this._fragmentPressCandidateId = id;
      this._fragmentPressIntentTimer = window.setTimeout(() => {
        if (this._fragmentPressCandidateId !== id) return;
        this._fragmentPressVisibleAt = window.performance ? window.performance.now() : Date.now();
        this.pressedFragmentId = id;
      }, 120);
    },
    finishFragmentPress(id) {
      window.clearTimeout(this._fragmentPressIntentTimer);
      this._fragmentPressCandidateId = '';
      if (this.pressedFragmentId !== id) return;
      const now = window.performance ? window.performance.now() : Date.now();
      const elapsed = now - (this._fragmentPressVisibleAt || now);
      const feedbackFloor = Math.max(0, 120 - elapsed);
      const remaining = Math.max(feedbackFloor, 150 - elapsed);

      this._fragmentReleasedPressId = id;
      this._fragmentActivationDelay = remaining + (this.prefersReducedMotion() ? 0 : 180);

      window.clearTimeout(this._fragmentPressTimer);
      if (remaining === 0) {
        this.pressedFragmentId = '';
        return;
      }

      this._fragmentPressTimer = window.setTimeout(() => {
        if (this.pressedFragmentId === id) this.pressedFragmentId = '';
      }, remaining);
    },
    cancelFragmentPress(id) {
      window.clearTimeout(this._fragmentPressIntentTimer);
      this._fragmentPressCandidateId = '';
      this._fragmentReleasedPressId = '';
      this._fragmentActivationDelay = 0;
      if (this.pressedFragmentId !== id) return;
      window.clearTimeout(this._fragmentPressTimer);
      this.pressedFragmentId = '';
    },
    currentHashId() {
      const routeHash = this.$route && this.$route.hash ? this.$route.hash : '';
      const browserHash = typeof window !== 'undefined' ? window.location.hash : '';
      let raw = routeHash;

      // In hash-router URLs the browser hash can look like "#/fragments#entry-id".
      // Vue usually exposes the second hash as this.$route.hash, but parse it here too
      // so direct browser/hashchange paths are safe.
      if (!raw && browserHash.startsWith('#/')) {
        const secondHash = browserHash.indexOf('#', 1);
        if (secondHash >= 0) raw = browserHash.slice(secondHash);
      }
      if (!raw) raw = browserHash;
      if (!raw) return '';

      try {
        return decodeURIComponent(raw.replace(/^#/, ''));
      } catch (err) {
        return raw.replace(/^#/, '');
      }
    },
    fragmentById(id) {
      return fragments.find((fragment) => fragment.id === id);
    },
    escapeHtml(text) {
      return String(text == null ? '' : text)
        .split('&').join('&amp;')
        .split('<').join('&lt;')
        .split('>').join('&gt;')
        .split(String.fromCharCode(34)).join('&quot;')
        .split("'").join('&#39;');
    },
    escapeRegExp(text) {
      return String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    fragmentSearchText(fragment) {
      return [
        fragment.title,
        fragment.teaser,
        ...(Array.isArray(fragment.body) ? fragment.body : [])
      ].join(' ').toLowerCase();
    },
    fragmentMatches(fragment) {
      const terms = this.searchTerms;
      if (!terms.length) return false;
      const text = this.fragmentSearchText(fragment);
      return terms.every((term) => text.includes(term));
    },
    highlightText(text) {
      const escaped = this.escapeHtml(text);
      const terms = [...new Set(this.searchTerms)]
        .sort((a, b) => b.length - a.length)
        .map((term) => this.escapeRegExp(term));
      if (!terms.length) return escaped;

      const re = new RegExp(`(${terms.join('|')})`, 'gi');
      return escaped.replace(re, '<mark>$1</mark>');
    },
    clearSearch() {
      this.searchQuery = '';
    },
    activateHashFragment(shouldScroll = true) {
      if (!this.fragmentSurfaceActive || this.$route.path !== '/fragments') return;
      const id = this.currentHashId();
      if (!id) return;

      const fragment = this.fragmentById(id);
      if (!fragment) return;

      if (fragment.category && this.activeCategory !== fragment.category) {
        this.activeCategory = fragment.category;
      }

      if (!this.openIds.includes(id)) {
        this.openIds = [id];
      }

      if (!shouldScroll) return;

      this.$nextTick(() => {
        this.scrollToHashFragment(id, 0);
      });
    },
    scrollToHashFragment(id, attempt = 0) {
      if (!this.fragmentSurfaceActive || this.$route.path !== '/fragments') return;
      window.clearTimeout(this._fragmentLandingTimer);
      const el = document.getElementById(id);
      if (!el) {
        if (attempt < 20) {
          this._fragmentLandingTimer = window.setTimeout(() => this.scrollToHashFragment(id, attempt + 1), 80);
        }
        return;
      }

      this._fragmentLandingTimer = window.setTimeout(() => {
        if (!this.fragmentSurfaceActive || this.$route.path !== '/fragments') return;
        settleScrollToId(id);
        window.dispatchEvent(new Event('scroll'));
      }, 120);
    },
    async setCategory(category) {
      if (this.activeCategory === category) return;
      if (this._categoryTransition && this._categoryTransition.skipTransition) {
        this._categoryTransition.skipTransition();
      }

      const applyCategory = async () => {
        this.activeCategory = category;
        this.openIds = [];
        await this.$nextTick();
      };
      const reduceMotion = this.prefersReducedMotion();
      if (!document.startViewTransition || reduceMotion || this.categoryKeyboard) {
        await applyCategory();
        return;
      }

      const transitionToken = (this._categoryTransitionToken || 0) + 1;
      this._categoryTransitionToken = transitionToken;
      const releaseCursorLock = acquireInteractiveCursorLock();
      this._releaseCategoryCursorLock = releaseCursorLock;
      this.categoryTransitionActive = true;
      document.documentElement.style.setProperty(
        '--fragment-category-direction',
        category === 'life' ? '1' : '-1'
      );
      await this.$nextTick();
      if (!this.fragmentSurfaceActive || this.$route.path !== '/fragments') {
        releaseCursorLock();
        return;
      }

      let transition;
      try {
        transition = document.startViewTransition(applyCategory);
      } catch (error) {
        releaseCursorLock();
        this.categoryTransitionActive = false;
        document.documentElement.style.removeProperty('--fragment-category-direction');
        await applyCategory();
        return;
      }
      this._categoryTransition = transition;
      transition.finished.catch(() => {}).finally(() => {
        releaseCursorLock();
        if (this._releaseCategoryCursorLock === releaseCursorLock) this._releaseCategoryCursorLock = null;
        if (this._categoryTransitionToken !== transitionToken) return;
        this._categoryTransition = null;
        this.categoryTransitionActive = false;
        document.documentElement.style.removeProperty('--fragment-category-direction');
      });
    },
    isOpen(id) {
      return this.openIds.includes(id);
    },
    toggleFragment(id) {
      if (this.isOpen(id)) {
        this.closeFragment();
        return;
      }

      window.clearTimeout(this._fragmentOpenTimer);
      const isPointerPress = this.pressedFragmentId === id || this._fragmentReleasedPressId === id;
      const activationDelay = isPointerPress ? (this._fragmentActivationDelay || 180) : 0;
      this._fragmentReleasedPressId = '';
      this._fragmentActivationDelay = 0;

      this._fragmentOpenTimer = window.setTimeout(() => {
        if (!this.fragmentSurfaceActive || this.$route.path !== '/fragments') return;
        this.pressedFragmentId = '';
        this._fragmentOpenFrame = window.requestAnimationFrame(() => {
          if (!this.fragmentSurfaceActive || this.$route.path !== '/fragments') return;
          const source = document.getElementById(id);
          this._fragmentSourceRect = source ? source.getBoundingClientRect() : null;
          this._fragmentReturnTarget = source && source.querySelector('.fragment-summary');
          this.openIds = [id];
          this.$nextTick(() => this.animateFragmentOpen());
        });
      }, activationDelay);
    },
    handleFragmentKeydown(event) {
      if (event.key === 'Escape' && this.activeFragment) this.closeFragment();
    },
    prefersReducedMotion() {
      return window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
    fragmentOriginTransform(sourceRect, paperRect) {
      if (!sourceRect || !paperRect) return 'translate3d(0, 14px, 0) scale(0.97)';
      const dx = sourceRect.left + sourceRect.width / 2 - (paperRect.left + paperRect.width / 2);
      const dy = sourceRect.top + sourceRect.height / 2 - (paperRect.top + paperRect.height / 2);
      const scale = Math.max(0.72, Math.min(0.96, sourceRect.width / paperRect.width));
      return `translate3d(${dx}px, ${dy}px, 0) scale(${scale})`;
    },
    trackFragmentAnimation(animation) {
      this._fragmentAnims.add(animation);
      animation.finished.catch(() => {}).finally(() => this._fragmentAnims.delete(animation));
      return animation;
    },
    animateFragmentOpen() {
      if (!this.fragmentSurfaceActive) return;
      const paper = this.$refs.floatingPaper;
      const overlay = this.$refs.fragmentOverlay;
      if (!paper || !overlay) return;

      const reduceMotion = this.prefersReducedMotion();
      const origin = this.fragmentOriginTransform(this._fragmentSourceRect, paper.getBoundingClientRect());
      this.trackFragmentAnimation(overlay.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: reduceMotion ? 120 : 220, easing: 'ease-out', fill: 'both' }
      ));
      const paperAnimation = this.trackFragmentAnimation(paper.animate(
        reduceMotion
          ? [{ opacity: 0 }, { opacity: 1 }]
          : [
            { transform: origin, opacity: 0.18, filter: 'blur(2px)' },
            { transform: 'translate3d(0, 0, 0) scale(1)', opacity: 1, filter: 'blur(0)' }
          ],
        { duration: reduceMotion ? 140 : 340, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'both' }
      ));
      const version = this._fragmentPresentationVersion;
      paperAnimation.finished.then(() => {
        if (!this.fragmentSurfaceActive || this._fragmentPresentationVersion !== version) return;
        const close = paper.querySelector('.fragment-floating-close');
        if (close) close.focus({ preventScroll: true });
      }).catch(() => {});
    },
    closeFragment() {
      const paper = this.$refs.floatingPaper;
      const overlay = this.$refs.fragmentOverlay;
      if (!paper || !overlay) {
        this.openIds = [];
        return;
      }

      this._fragmentAnims.forEach((animation) => animation.cancel());
      this._fragmentAnims.clear();
      const source = this.activeFragment ? document.getElementById(this.activeFragment.id) : null;
      const sourceRect = source ? source.getBoundingClientRect() : this._fragmentSourceRect;
      const target = this.fragmentOriginTransform(sourceRect, paper.getBoundingClientRect());
      const reduceMotion = this.prefersReducedMotion();
      if (source) this.restoringFragmentId = source.id;

      this.trackFragmentAnimation(overlay.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration: reduceMotion ? 100 : 190, easing: 'ease-out', fill: 'both' }
      ));
      const paperAnimation = this.trackFragmentAnimation(paper.animate(
        reduceMotion
          ? [{ opacity: 1 }, { opacity: 0 }]
          : [
            { transform: 'translate3d(0, 0, 0) scale(1)', opacity: 1, filter: 'blur(0)' },
            { transform: target, opacity: 0.08, filter: 'blur(2px)' }
          ],
        { duration: reduceMotion ? 110 : 250, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'both' }
      ));
      const version = this._fragmentPresentationVersion;
      paperAnimation.finished.then(() => {
        if (!this.fragmentSurfaceActive || this._fragmentPresentationVersion !== version) return;
        this.openIds = [];
        this.$nextTick(() => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              this.restoringFragmentId = '';
            });
          });
          if (this._fragmentReturnTarget && this._fragmentReturnTarget.isConnected) {
            this._fragmentReturnTarget.focus({ preventScroll: true });
          }
        });
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.fragments-hero-image {
  display: grid;
  margin: 1.4rem auto 0;
  max-width: min(100%, 640px);
}

.fragments-hero-image img {
  grid-area: 1 / 1;
  width: 100%;
  height: auto;
  transition: opacity var(--theme-duration, 220ms) ease;
}

.fragments-page {
  --fragments-control-width: 15.5rem;
  --fragments-heading: #333333;
  --fragments-muted: #686868;
  --fragments-body: #505050;
  --fragments-cue: #333333;
  --fragments-hero-line: rgba(80, 80, 80, 0.16);
  --fragments-toggle-border: rgba(80, 80, 80, 0.3);
  --fragments-toggle-bg: rgba(235, 235, 238, 0.96);
  --fragments-toggle-shadow: none;
  --fragments-toggle-thumb: rgba(0, 0, 0, 0.72);
  --fragments-toggle-option: #6f6f72;
  --fragments-toggle-option-hover: #29292b;
  --fragments-toggle-selected: #ffffff;
  --fragments-search-line: rgba(80, 80, 80, 0.42);
  --fragments-search-line-hover: rgba(80, 80, 80, 0.72);
  --fragments-search-line-focus: rgba(80, 80, 80, 0.95);
  --fragments-search-placeholder: rgba(80, 80, 80, 0.5);
  --fragments-search-placeholder-hover: rgba(80, 80, 80, 0.68);
  --fragments-mark-bg: rgba(80, 80, 80, 0.14);
  --fragments-card-border: rgba(80, 80, 80, 0.15);
  --fragments-card-hover-border: rgba(80, 80, 80, 0.28);
  --fragments-card-bg: rgba(0, 0, 0, 0.025);
  /* Just a touch lighter than the original hover: a gentler dark mix, with
     the card still reading deeper than the resting tint. */
  --fragments-card-hover-bg: color-mix(in srgb, var(--fragments-card-bg) 97%, var(--fragments-heading) 3%);
  --fragments-card-shadow: none;
  --fragments-card-hover-shadow: none;
  --fragments-paper-1: rgba(250, 250, 250, 0.94);
  --fragments-paper-2: rgba(246, 246, 246, 0.94);
  --fragments-paper-3: rgba(248, 248, 248, 0.94);
  --fragments-paper-edge: rgba(80, 80, 80, 0.14);
  --fragments-paper-shadow: 0 8px 24px rgba(32, 32, 32, 0.055), 0 1px 2px rgba(32, 32, 32, 0.05);
  --fragments-body-line: rgba(80, 80, 80, 0.12);
  --fragments-focus-ring: rgba(80, 80, 80, 0.45);
  padding: 0 0 2.5rem;
  color: var(--fragments-body);
}

.fragments-hero { display: grid; grid-template-columns: minmax(0, 5fr) minmax(0, 6fr); align-items: center; gap: 32px 44px; margin: 30px 0 8px; }
.fragments-hero-copy { min-width: 0; }
.fragments-hero .fragments-hero-image { width: 100%; margin: 0; }
.fragments-hero .fragments-hero-image img { width: 100%; height: auto; object-fit: contain; }


.fragments-hero h1 {
  margin: 0 0 20px;
  color: var(--fragments-heading);
}

.fragments-intro {
  margin: 0;
  max-width: none;
  color: var(--fragments-body);
  font: inherit;
}

.fragments-controls {
  grid-column: 1 / -1;
  margin: 12px 0 0;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--fragments-hero-line);
  width: 100%;
  max-width: none;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.05rem 1.2rem;
}

.category-segmented { display: flex; gap: 24px; align-items: center; max-width: 100%; }
.category-option {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.38rem;
  flex: 0 0 auto;
  border: 0;
  border-radius: 0;
  min-height: 44px;
  padding: 0;
  background: transparent;
  color: var(--fragments-toggle-option);
  cursor: var(--site-interactive-cursor);
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 16px;
  line-height: 1;
  text-align: center;
  transition:
    color 160ms ease,
    background-color 160ms ease,
    transform 140ms cubic-bezier(0.23, 1, 0.32, 1);
}

.category-option:active { transform: scale(0.96); }
.category-option:focus-visible {
  outline: 2px solid var(--fragments-focus-ring);
  outline-offset: 2px;
}
/* The same quiet wash-and-brighten as the chapter rail on research/builds:
   the hovered option gains a faint tint and its text steps up a shade. */
@media (hover: hover) and (pointer: fine) {
  .category-option:hover {
    color: var(--fragments-toggle-option-hover);
    background: none;
  }
  body.dark-mode .category-option:hover {
    background: none;
  }
}
.category-segmented:not(.life) .category-ideas,
.category-segmented.life .category-life {
  color: var(--fragments-heading);
}
.category-count {
  min-width: 1.15rem;
  font-variant-numeric: tabular-nums;
  font-size: 0.66rem;
  opacity: 0.66;
}

.fragment-search {
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  flex: 0 0 var(--fragments-control-width);
  width: var(--fragments-control-width);
  max-width: 100%;
  margin-left: auto;
  padding: 0.1rem 1.45rem 0.28rem 0;
  border-bottom: 1px solid var(--fragments-search-line);
  color: var(--fragments-cue);
  cursor: var(--site-text-cursor);
  transition:
    border-bottom-color 180ms ease,
    color 180ms ease;
}

/* Same interaction language as the timeline searches: hover steps the
   underline, icon, and placeholder toward lit; focus lights them fully. */
@media (hover: hover) and (pointer: fine) {
  .fragment-search:hover {
    border-bottom-color: var(--fragments-search-line-hover);
  }
  .fragment-search:hover .fragment-search-icon {
    opacity: 0.85;
  }
  .fragment-search:hover .fragment-search-input::placeholder {
    color: var(--fragments-search-placeholder-hover);
  }
}
.fragment-search:focus-within {
  border-bottom-color: var(--fragments-search-line-focus);
}
.fragment-search:focus-within .fragment-search-icon {
  opacity: 1;
}

.fragment-search-input {
  width: 100%;
  border: 0;
  outline: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.92rem;
  line-height: 1.25;
  -webkit-user-select: text;
  user-select: text;
}

.fragment-search-input::placeholder {
  color: var(--fragments-search-placeholder);
  transition: color 180ms ease;
}

.fragment-search-input::-webkit-search-decoration,
.fragment-search-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
}

.fragment-search-icon {
  position: absolute;
  top: 50%;
  right: 0.05rem;
  transform: translateY(-50%);
  font-size: 0.72rem;
  opacity: 0.58;
  pointer-events: none;
  transition: opacity 180ms ease;
}

.fragment-list { display: block; padding: 0; }
.fragment-card {
  --paper-background: var(--fragments-paper-1);
  position: relative;
  z-index: 1;
  isolation: isolate;
  scroll-margin-top: 20px;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  overflow: visible;
  transition: color 180ms ease;
}

</style>

<style>

::view-transition-group(fragment-category-card-0),
::view-transition-group(fragment-category-card-1),
::view-transition-group(fragment-category-card-2),
::view-transition-group(fragment-category-card-3),
::view-transition-group(fragment-category-card-4),
::view-transition-group(fragment-category-card-5),
::view-transition-group(fragment-category-card-6),
::view-transition-group(fragment-category-card-7),
::view-transition-group(fragment-category-card-8),
::view-transition-group(fragment-category-card-9),
::view-transition-group(fragment-category-card-10),
::view-transition-group(fragment-category-card-11),
::view-transition-group(fragment-category-card-12),
::view-transition-group(fragment-category-card-13),
::view-transition-group(fragment-category-card-14),
::view-transition-group(fragment-category-card-15),
::view-transition-group(fragment-category-card-16),
::view-transition-group(fragment-category-card-17),
::view-transition-group(fragment-category-card-18),
::view-transition-group(fragment-category-card-19) {
  animation-duration: 320ms;
  animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
}

::view-transition-old(fragment-category-card-0),
::view-transition-old(fragment-category-card-1),
::view-transition-old(fragment-category-card-2),
::view-transition-old(fragment-category-card-3),
::view-transition-old(fragment-category-card-4),
::view-transition-old(fragment-category-card-5),
::view-transition-old(fragment-category-card-6),
::view-transition-old(fragment-category-card-7),
::view-transition-old(fragment-category-card-8),
::view-transition-old(fragment-category-card-9),
::view-transition-old(fragment-category-card-10),
::view-transition-old(fragment-category-card-11),
::view-transition-old(fragment-category-card-12),
::view-transition-old(fragment-category-card-13),
::view-transition-old(fragment-category-card-14),
::view-transition-old(fragment-category-card-15),
::view-transition-old(fragment-category-card-16),
::view-transition-old(fragment-category-card-17),
::view-transition-old(fragment-category-card-18),
::view-transition-old(fragment-category-card-19) {
  animation: fragment-category-out 150ms cubic-bezier(0.4, 0, 1, 1) both;
}

::view-transition-new(fragment-category-card-0),
::view-transition-new(fragment-category-card-1),
::view-transition-new(fragment-category-card-2),
::view-transition-new(fragment-category-card-3),
::view-transition-new(fragment-category-card-4),
::view-transition-new(fragment-category-card-5),
::view-transition-new(fragment-category-card-6),
::view-transition-new(fragment-category-card-7),
::view-transition-new(fragment-category-card-8),
::view-transition-new(fragment-category-card-9),
::view-transition-new(fragment-category-card-10),
::view-transition-new(fragment-category-card-11),
::view-transition-new(fragment-category-card-12),
::view-transition-new(fragment-category-card-13),
::view-transition-new(fragment-category-card-14),
::view-transition-new(fragment-category-card-15),
::view-transition-new(fragment-category-card-16),
::view-transition-new(fragment-category-card-17),
::view-transition-new(fragment-category-card-18),
::view-transition-new(fragment-category-card-19) {
  animation: fragment-category-in 300ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes fragment-category-out {
  to {
    opacity: 0;
    transform: translateX(calc(var(--fragment-category-direction, 1) * -7px)) scale(0.994);
  }
}

@keyframes fragment-category-in {
  from {
    opacity: 0;
    transform: translateX(calc(var(--fragment-category-direction, 1) * 9px)) scale(0.994);
  }
}

</style>

<style scoped>

.fragment-entry-surface::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: 12px -18px;
  background: var(--fragments-card-hover-bg);
  opacity: 0;
  pointer-events: none;
  transition: opacity 180ms ease;
}
@media (hover: hover) and (pointer: fine) {
  .fragment-summary:hover .fragment-entry-surface::before { opacity: 1; }
  .fragment-summary:hover .fragment-entry-surface {
    transform: translateY(-2px) rotate(-0.65deg);
  }
}
.fragment-card:first-child .fragment-summary { border-top-color: transparent; }

.fragment-card.is-lifted {
  pointer-events: none;
}

.fragment-card.is-restoring .fragment-summary {
  opacity: 1;
  transition: none;
}

.fragment-summary {
  position: relative;
  z-index: 1;
  border-top: 1px solid var(--fragments-hero-line);
  cursor: var(--site-interactive-cursor);
  outline: none;
  scale: 1;
  touch-action: manipulation;
  transform: translate3d(0, 0, 0) scale(1);
  transform-origin: center;
  transition: transform 180ms cubic-bezier(0.23, 1, 0.32, 1), opacity 180ms ease;
}

/* Keep the hit area still while the entry and its wash turn together. */
.fragment-entry-surface {
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 6fr) auto;
  column-gap: 36px;
  align-items: baseline;
  padding: 38px 0 42px;
  pointer-events: none;
  transform: translateY(0) rotate(0deg);
  transform-origin: 50% 50%;
  transition: transform 260ms cubic-bezier(0.23, 1, 0.32, 1);
}

.fragment-summary.is-pressed {
  scale: 1;
  transform: translate3d(0, 2px, 0) scale(0.995);
  transition-duration: 70ms;
  transition-timing-function: cubic-bezier(0.32, 0, 0.67, 0);
}

.fragment-summary:focus-visible {
  outline: 2px solid var(--fragments-focus-ring);
  outline-offset: -2px;
  border-radius: 0;
}

.fragment-summary h2 {
  margin: 0;
  font-family: Georgia, "PT Serif", serif;
  font-size: 24px;
  font-weight: 400;
  letter-spacing: -0.012em;
  line-height: 1.35;
  color: var(--fragments-heading);
}

.fragment-teaser {
  margin: 0;
  color: var(--fragments-muted);
  font-family: "PT Serif", Georgia, serif;
  font-size: 15px;
  line-height: 1.7;
  transition: color 220ms ease;
}

.read-cue {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0;
  white-space: nowrap;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.82rem;
  color: var(--fragments-muted);
  transition: color 220ms ease;
}

.fragment-read-arrow { display: flex; transition: transform 240ms cubic-bezier(.23,1,.32,1); }
.fragment-read-arrow :deep(svg) { width: 14px; height: 14px; }
.fragment-preview-mark { position: relative; display: inline-grid; place-items: center; width: 24px; height: 28px; margin-block: -7px; }
.fragment-preview-mark .fragment-read-arrow { grid-area: 1 / 1; transition: transform 220ms cubic-bezier(.23,1,.32,1), opacity 160ms ease; }
.fragment-preview-note { position: absolute; inset: 0; width: 24px; height: 28px; opacity: 0; transform: translateY(4px) rotate(8deg) scale(.88); transition: transform 260ms cubic-bezier(.23,1,.32,1), opacity 180ms ease; }
@media (hover: hover) and (pointer: fine) {
  .fragment-summary:hover .read-cue { color: var(--fragments-heading); }
  .fragment-summary:hover .fragment-read-arrow { transform: translate(2px,-2px); }
  .is-note-preview .fragment-summary:hover .fragment-read-arrow { transform: translate(7px,-8px); opacity: 0; }
  .is-note-preview .fragment-summary:hover .fragment-preview-note { transform: translateY(-1px) rotate(-5deg); opacity: 1; }
}
.fragment-summary:focus-visible .fragment-entry-surface::before { opacity: 1; transition: none; }
@media (max-width: 800px) { .fragments-hero { grid-template-columns: 1fr; gap: 24px; } .fragments-hero-image { max-width: 680px; justify-self: center; } .fragment-entry-surface { column-gap: 24px; } }
@media (max-width: 630px) { .fragment-entry-surface { grid-template-columns: 1fr; gap: 16px; padding: 28px 0 32px; } .fragment-summary h2 { font-size: 24px; } }


.fragment-body {
  position: relative;
  z-index: 1;
  padding: 0.1rem 1.1rem 1.15rem;
}

.fragment-body p {
  margin: 1rem 0 0;
  color: var(--fragments-body);
  font-family: "PT Serif", Georgia, serif;
  font-size: 0.96rem;
  line-height: 1.78;
  white-space: pre-line;
}

.fragment-summary :deep(mark),
.fragment-body :deep(mark) {
  background: var(--fragments-mark-bg);
  color: inherit;
  border-radius: 2px;
  padding: 0 1px;
}

.fragments-empty {
  margin: 1.25rem 0 0;
  color: var(--fragments-muted);
  font-size: 0.96rem;
}

@media (max-width: 630px) {
  .fragments-controls {
    align-items: flex-start;
    flex-direction: column;
    justify-content: flex-start;
    gap: 1.05rem;
  }

  .category-segmented {
    align-self: flex-start;
  }

  .fragment-search {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    flex-basis: auto;
    margin-left: auto;
    margin-right: auto;
    align-self: flex-start;
  }

  .fragment-summary,
  .fragment-body {
    padding-left: 0;
    padding-right: 0;
  }

  .fragment-list {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding-right: 0;
    padding-left: 0;
  }

  .fragment-card:nth-child(3n + 2) {
    margin-top: 0;
  }

  .fragment-summary h2 {
    font-size: 24px;
  }

  .fragment-teaser,
  .fragment-body p {
    font-size: 0.95rem;
  }
}
@media (prefers-reduced-motion: reduce) {
  .category-thumb,
  .category-option,
  .fragment-card,
  .fragment-summary {
    transition: color 120ms ease, opacity 120ms ease, background-color 120ms ease;
  }
  .fragment-summary.is-pressed {
    transform: none;
    opacity: 0.82;
  }

  .fragment-entry-surface::before { transition: none; }
  .fragment-entry-surface,
  .fragment-summary:hover .fragment-entry-surface { transform: none; transition: none; }
  .fragment-read-arrow,
  .fragment-summary:hover .fragment-read-arrow { transform: none; transition: none; }
  .is-note-preview .fragment-summary:hover .fragment-read-arrow,
  .is-note-preview .fragment-summary:hover .fragment-preview-note { transform: none; transition: none; }
}
</style>

<style>
.fragment-overlay {
  position: fixed;
  z-index: 9200;
  inset: 0;
  display: grid;
  place-items: center;
  padding: clamp(1rem, 4vw, 3rem);
  background: rgba(24, 24, 24, 0.28);
  backdrop-filter: blur(8px) saturate(92%);
  -webkit-backdrop-filter: blur(8px) saturate(92%);
}

.fragment-floating-paper {
  position: relative;
  box-sizing: border-box;
  width: min(44rem, 100%);
  max-height: min(82vh, 54rem);
  padding: clamp(1.35rem, 4vw, 2.5rem);
  overflow: auto;
  border: 0;
  border-radius: 1px;
  background: rgba(250, 250, 250, 0.985);
  box-shadow: 0 28px 90px rgba(24, 24, 24, 0.22), 0 3px 12px rgba(24, 24, 24, 0.1);
  color: #505050;
  overscroll-behavior: contain;
  transform-origin: center;
}

.fragment-floating-close {
  position: sticky;
  z-index: 2;
  top: 0;
  float: right;
  display: grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
  margin: -0.35rem -0.35rem 0.35rem 1rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(80, 80, 80, 0.08);
  color: #505050;
  font: 300 1.2rem/1 "Quattrocento Sans", sans-serif;
  cursor: var(--site-interactive-cursor);
  transition: color 140ms ease, background-color 140ms ease, transform 120ms cubic-bezier(0.23, 1, 0.32, 1);
}

.fragment-floating-close:active { transform: scale(0.9); }
.fragment-floating-close:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
  .fragment-floating-close:hover {
    background: rgba(80, 80, 80, 0.14);
    color: #202020;
  }
}

.fragment-floating-kind {
  margin: 0 0 1.1rem;
  color: #858585;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.fragment-floating-paper h2 {
  margin: 0;
  color: #333333;
  font-family: Georgia, "PT Serif", serif;
  font-size: clamp(1.5rem, 4vw, 2.15rem);
  font-weight: 700;
  letter-spacing: -0.018em;
  line-height: 1.14;
  text-wrap: balance;
}

.fragment-floating-teaser {
  margin: 0.85rem 0 0;
  padding-bottom: 1.15rem;
  border-bottom: 1px solid rgba(80, 80, 80, 0.14);
  color: #686868;
  font-family: "PT Serif", Georgia, serif;
  font-size: 1rem;
  line-height: 1.58;
}

.fragment-floating-body p {
  margin: 1.1rem 0 0;
  color: #505050;
  font-family: "PT Serif", Georgia, serif;
  font-size: 0.98rem;
  line-height: 1.78;
  white-space: pre-line;
}

.fragment-floating-paper mark {
  padding: 0 1px;
  border-radius: 2px;
  background: rgba(80, 80, 80, 0.14);
  color: inherit;
}

body.dark-mode .fragment-overlay {
  background: rgba(0, 0, 0, 0.46);
}

body.dark-mode .fragment-floating-paper {
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(35, 35, 37, 0.985);
  box-shadow: 0 30px 100px rgba(0, 0, 0, 0.5), 0 3px 14px rgba(0, 0, 0, 0.28);
  color: #d2d2d2;
}

body.dark-mode .fragment-floating-close {
  background: rgba(255, 255, 255, 0.08);
  color: #d5d5d5;
}

body.dark-mode .fragment-floating-kind { color: #929292; }
body.dark-mode .fragment-floating-paper h2 { color: #ececec; }
body.dark-mode .fragment-floating-teaser {
  border-bottom-color: rgba(255, 255, 255, 0.12);
  color: #b8b8b8;
}
body.dark-mode .fragment-floating-body p { color: #d0d0d0; }
body.dark-mode .fragment-floating-paper mark { background: rgba(255, 255, 255, 0.16); }

@media (max-width: 630px) {
  .fragment-overlay {
    align-items: end;
    padding:
      max(0.65rem, env(safe-area-inset-top))
      max(0.65rem, env(safe-area-inset-right))
      max(0.65rem, env(safe-area-inset-bottom))
      max(0.65rem, env(safe-area-inset-left));
  }

  .fragment-floating-paper {
    width: 100%;
    max-height: calc(100dvh - 1.3rem - env(safe-area-inset-top) - env(safe-area-inset-bottom));
    padding: 1.3rem 1.05rem 1.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fragment-overlay {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
}

@media (prefers-reduced-transparency: reduce) {
  .fragment-overlay {
    background: rgba(24, 24, 24, 0.72);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  .fragment-floating-paper { background: #fafafa; }
  body.dark-mode .fragment-floating-paper { background: #232325; }
}

html.dark-mode .fragments-page,
body.dark-mode .fragments-page {
  --fragments-heading: #cccccc;
  --fragments-muted: var(--site-text-secondary);
  --fragments-body: #cfcfcf;
  --fragments-cue: #cccccc;
  --fragments-hero-line: rgba(204, 204, 204, 0.16);
  --fragments-toggle-border: rgba(204, 204, 204, 0.3);
  --fragments-toggle-bg: rgba(35, 35, 38, 0.98);
  --fragments-toggle-shadow: none;
  --fragments-toggle-thumb: rgba(255, 255, 255, 0.78);
  --fragments-toggle-option: var(--site-text-secondary);
  --fragments-toggle-option-hover: #f1f1f2;
  --fragments-toggle-selected: #171719;
  --fragments-search-line: rgba(204, 204, 204, 0.42);
  --fragments-search-line-hover: rgba(204, 204, 204, 0.7);
  --fragments-search-line-focus: rgba(204, 204, 204, 0.95);
  --fragments-search-placeholder: rgba(204, 204, 204, 0.52);
  --fragments-search-placeholder-hover: rgba(204, 204, 204, 0.72);
  --fragments-mark-bg: rgba(255, 255, 255, 0.16);
  --fragments-card-border: rgba(204, 204, 204, 0.15);
  --fragments-card-hover-border: rgba(204, 204, 204, 0.28);
  --fragments-card-bg: rgba(255, 255, 255, 0.035);
  --fragments-card-hover-bg: color-mix(in srgb, var(--fragments-card-bg) 96%, var(--fragments-heading) 4%);
  --fragments-card-shadow: none;
  --fragments-card-hover-shadow: none;
  --fragments-paper-1: rgba(37, 37, 39, 0.97);
  --fragments-paper-2: rgba(40, 40, 42, 0.97);
  --fragments-paper-3: rgba(35, 35, 37, 0.97);
  --fragments-paper-edge: rgba(255, 255, 255, 0.11);
  --fragments-paper-shadow: 0 10px 28px rgba(0, 0, 0, 0.18), 0 1px 2px rgba(0, 0, 0, 0.24);
  --fragments-body-line: rgba(204, 204, 204, 0.12);
  --fragments-focus-ring: rgba(204, 204, 204, 0.45);
}

body.dark-mode .read-cue {
  color: rgba(204, 204, 204, 0.46);
}

html.theme-transitioning .fragment-card,
body.theme-transitioning .fragment-card {
  transition:
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease,
    color var(--theme-duration, 220ms) ease;
}

html.theme-transitioning .category-segmented,
body.theme-transitioning .category-segmented {
  transition:
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease;
}

html.theme-transitioning .category-thumb,
body.theme-transitioning .category-thumb {
  transition:
    background-color var(--theme-duration, 220ms) ease,
    transform 200ms cubic-bezier(0.23, 1, 0.32, 1);
}

html.theme-transitioning .category-option,
body.theme-transitioning .category-option {
  transition: color var(--theme-duration, 220ms) ease;
}

html.theme-transitioning .fragment-search,
html.theme-transitioning .fragment-search-input,
html.theme-transitioning .fragment-search-icon,
body.theme-transitioning .fragment-search,
body.theme-transitioning .fragment-search-input,
body.theme-transitioning .fragment-search-icon {
  transition:
    color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease;
}

html.theme-transitioning .fragment-search-input::placeholder,
body.theme-transitioning .fragment-search-input::placeholder {
  transition: color var(--theme-duration, 220ms) ease;
}

html.theme-transitioning .fragments-hero,
html.theme-transitioning .fragments-hero h1,
html.theme-transitioning .fragments-intro,
html.theme-transitioning .fragment-summary,
html.theme-transitioning .fragment-summary h2,
html.theme-transitioning .fragment-teaser,
html.theme-transitioning .read-cue,
html.theme-transitioning .fragment-body p,
html.theme-transitioning .fragments-empty,
body.theme-transitioning .fragments-hero,
body.theme-transitioning .fragments-hero h1,
body.theme-transitioning .fragments-intro,
body.theme-transitioning .fragment-summary,
body.theme-transitioning .fragment-summary h2,
body.theme-transitioning .fragment-teaser,
body.theme-transitioning .read-cue,
body.theme-transitioning .fragment-body p,
body.theme-transitioning .fragments-empty {
  transition: color var(--theme-duration, 220ms) ease;
}
</style>
